import { inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type Theme = 'light' | 'dark';

/** Shared with the WorkAssist portal and FP&A — same key, same values. */
const STORAGE_KEY = 'was_theme';

/** Applied when nothing is stored yet. */
const DEFAULT_THEME: Theme = 'light';

/**
 * App theme, persisted per browser.
 *
 * The chosen theme is written to `data-theme` on <html>, which is what the
 * palettes in styles.scss key off. Kept out of component state so the setting
 * survives navigation between shell pages.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  readonly theme = signal<Theme>(DEFAULT_THEME);

  constructor() {
    this.apply(this.read());
  }

  toggle(): void {
    this.apply(this.theme() === 'dark' ? 'light' : 'dark');
  }

  set(theme: Theme): void {
    this.apply(theme);
  }

  /** Label for the control that switches to the *other* theme. */
  get toggleLabel(): string {
    return this.theme() === 'dark' ? 'Light mode' : 'Dark mode';
  }

  private apply(theme: Theme): void {
    this.theme.set(theme);
    this.document.documentElement.setAttribute('data-theme', theme);
    try {
      this.document.defaultView?.localStorage?.setItem(STORAGE_KEY, theme);
    } catch {
      // Private browsing or blocked storage — the theme still applies for this
      // session, it just will not be remembered.
    }
  }

  private read(): Theme {
    try {
      const stored = this.document.defaultView?.localStorage?.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') return stored;
    } catch {
      // Ignore and fall through to the default.
    }
    return DEFAULT_THEME;
  }
}
